import { collection, doc, setDoc, getDoc, getDocs, query, where, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import * as CardTemplatesService from './cardTemplatesService';
import { updateAppStatus } from './appStatusService';
import { AppStatus } from '../constants/appStatus';

const DEPLOYMENTS_COLLECTION = 'appDeployments';

/**
 * ממפה בין שלב (stage) נוכחי של הדיפלוימנט לסטטוס המתאים ב-userApps
 * @param {string} currentStage - השלב הנוכחי בתהליך הדיפלוימנט
 * @returns {string} - סטטוס מתאים עבור userApps
 */
const mapDeploymentStageToAppStatus = (currentStage) => {
  // מיפוי שלבים לפי הערכים המוגדרים ב-AppStatus
  const stageMap = {
    'app_setup': AppStatus.DEPLOYMENT_STARTED,
    'internal_testing': AppStatus.INTERNAL_TESTING,
    'closed_testing': AppStatus.CLOSED_TESTING,
    'production': AppStatus.PRODUCTION_RELEASE,
    'app_live': AppStatus.APP_LIVE
  };
  
  return stageMap[currentStage] || AppStatus.DEPLOYMENT_STARTED;
};

/**
 * Service for managing application deployments in Firebase
 */
class AppDeploymentsService {
  /**
   * Get deployment by file ID
   * @param {string} fileId - The file identifier
   * @returns {Promise<Object|null>} - Deployment details or null if not found
   */
  async getDeploymentByFileId(fileId) {
    try {
      if (!fileId) {
        console.error('getDeploymentByFileId: No fileId provided');
        return null;
      }
      
      const q = query(
        collection(db, DEPLOYMENTS_COLLECTION),
        where('fileId', '==', fileId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log(`No deployment found for fileId: ${fileId}`);
        return null;
      }
      
      // Return the first document found with the ID
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      console.error('Error getting deployment by fileId:', error);
      throw error;
    }
  }
  
  /**
   * Create a new application deployment
   * @param {Object} deploymentData - Deployment data
   * @returns {Promise<string>} - ID of the created deployment
   */
  async createDeployment(deploymentData) {
    try {
      console.log('Creating deployment with data:', deploymentData);
      
      // Create a unique ID or use the file ID as the identifier
      const deploymentId = deploymentData.fileId;
      const deploymentRef = doc(db, DEPLOYMENTS_COLLECTION, deploymentId);
      
      // Load all card templates to automatically initialize stages
      console.log('Loading card templates to initialize stages');
      const cardTemplates = await CardTemplatesService.getAllCardTemplates();
      
      if (!cardTemplates || cardTemplates.length === 0) {
        console.error('No card templates found for initializing stages');
        throw new Error('No card templates available to create stages');
      }
      
      console.log(`Loaded ${cardTemplates.length} card templates`);
      
      // Initialize all possible stages with the correct structure
      const initialStages = {};
      
      // Only process templates of type 'stage' (which are the actual stages)
      cardTemplates
        .filter(template => template.type === 'stage')
        .forEach(template => {
          initialStages[template.id] = {
            id: template.id,
            title: template.title,
            status: 'pending',
            answers: {},
            substepsProgress: {},
            startedAt: null,
            completedAt: null
          };
          
          // If there are substeps, initialize them too
          if (template.substeps && Array.isArray(template.substeps)) {
            template.substeps.forEach(substep => {
              if (!initialStages[template.id].substepsProgress[substep.id]) {
                initialStages[template.id].substepsProgress[substep.id] = {
                  status: 'pending',
                  startedAt: null,
                  completedAt: null
                };
              }
            });
          }
        });
      
      console.log(`Initialized ${Object.keys(initialStages).length} stages`);
      
      const now = new Date().toISOString();
      
      const deploymentToSave = {
        ...deploymentData,
        stages: initialStages,
        createdAt: now,
        updatedAt: now,
        status: 'initialized', // Will change to 'in-progress' when the user starts the process
        startedAt: null,
        storeUrl: deploymentData.storeUrl || null // Changed from url to storeUrl
      };
      
      console.log('Final deployment data to save:', deploymentToSave);
      await setDoc(deploymentRef, deploymentToSave);
      console.log(`Deployment created with ID: ${deploymentId}`);
      
      return deploymentId;
    } catch (error) {
      console.error('Error creating deployment:', error);
      throw error;
    }
  }
  
  /**
   * Update an existing application deployment
   * @param {string} deploymentId - Deployment ID
   * @param {Object} updateData - Update data
   * @returns {Promise<boolean>} - Success status
   */
  async updateDeployment(deploymentId, updateData) {
    try {
      const deploymentRef = doc(db, DEPLOYMENTS_COLLECTION, deploymentId);
      const deploymentDoc = await getDoc(deploymentRef);
      
      if (!deploymentDoc.exists()) {
        throw new Error(`Deployment with ID ${deploymentId} not found`);
      }
      
      const currentData = deploymentDoc.data();
      
      // Merge complex update data (like stages)
      const mergedData = { ...updateData };
      
      // Handle formData - temporary form data
      if (updateData.formData) {
        // If the update includes formData, merge it with existing data
        mergedData.formData = {
          ...(currentData.formData || {}),
          ...updateData.formData 
        };
      }
      
      // Handle stages and substeps
      if (updateData.stages) {
        mergedData.stages = { ...currentData.stages };
        
        // Merge with existing stage data
        Object.entries(updateData.stages).forEach(([stageId, stageData]) => {
          mergedData.stages[stageId] = {
            ...(currentData.stages[stageId] || {}),
            ...stageData
          };
          
          // Specifically merge answers
          if (stageData.answers) {
            mergedData.stages[stageId].answers = {
              ...(currentData.stages[stageId]?.answers || {}),
              ...stageData.answers
            };
          }
          
          // Specifically merge substep progress
          if (stageData.substepsProgress) {
            mergedData.stages[stageId].substepsProgress = {
              ...(currentData.stages[stageId]?.substepsProgress || {}),
              ...stageData.substepsProgress
            };
          }
        });
      }
      
      // If updating status to in-progress and process hasn't started yet, set start time
      if (updateData.status === 'in-progress' && !currentData.startedAt) {
        mergedData.startedAt = updateData.startedAt || new Date().toISOString();
      }
      
      // Set update time
      mergedData.updatedAt = updateData.updatedAt || new Date().toISOString();
      
      // עדכון המסמך בפיירבייס
      await updateDoc(deploymentRef, mergedData);
      
      // עדכון userApps כאשר שלב נוכחי (currentStage) משתנה או שהסטטוס מתעדכן
      if ((updateData.currentStage || updateData.status) && currentData.fileId) {
        try {
          // מצא את מזהה המסמך של userApp לפי fileId
          const q = query(
            collection(db, 'userApps'),
            where('fileId', '==', currentData.fileId)
          );
          
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userAppDoc = querySnapshot.docs[0];
            let userAppStatus;
            
            if (updateData.currentStage) {
              // אם השלב הנוכחי התעדכן, המר אותו לסטטוס מתאים
              userAppStatus = mapDeploymentStageToAppStatus(updateData.currentStage);
            } else if (updateData.status) {
              // אם רק הסטטוס השתנה, בדוק את השלבים ומצא את השלב הראשון שאינו completed
              
              // שילוב של הנתונים הקיימים והעדכון
              const mergedStages = { ...currentData.stages };
              if (updateData.stages) {
                Object.entries(updateData.stages).forEach(([stageId, stageData]) => {
                  mergedStages[stageId] = { ...mergedStages[stageId], ...stageData };
                });
              }
              
              // רשימת שלבים ומיון לפי הסדר
              const cardTemplates = await CardTemplatesService.getAllCardTemplates();
              const orderedStages = cardTemplates
                .filter(template => template.type === 'stage')
                .sort((a, b) => a.order - b.order);
              
              // מצא את השלב הראשון שאינו completed
              let nextStage = null;
              for (const stage of orderedStages) {
                const stageId = stage.id;
                const deployStage = mergedStages[stageId];
                
                if (!deployStage || deployStage.status !== 'completed') {
                  nextStage = stageId;
                  break;
                }
              }
              
              if (nextStage) {
                userAppStatus = mapDeploymentStageToAppStatus(nextStage);
              } else {
                // אם כל השלבים הושלמו, סטטוס הוא APP_LIVE
                userAppStatus = AppStatus.APP_LIVE;
              }
            } else {
              // אם אין שינוי רלוונטי, לא נעדכן את הסטטוס
              return true;
            }
            
            // קרא לפונקציה שמעדכנת את הסטטוס בדוקומנט של userApps
            await updateAppStatus(
              userAppDoc.id, 
              userAppStatus, 
              `Updated from deployment ${updateData.currentStage ? 'stage change' : 'status update'}`
            );
          }
        } catch (error) {
          console.error('Error updating userApp status:', error);
          // לא נזרוק את השגיאה כדי לא לפגוע בהצלחת העדכון של ה-deployment
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error updating deployment:', error);
      throw error;
    }
  }
  
  /**
   * Get all active deployments
   * @returns {Promise<Array>} - List of deployments
   */
  async getAllActiveDeployments() {
    try {
      const q = query(
        collection(db, DEPLOYMENTS_COLLECTION),
        where('status', '==', 'in-progress')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting active deployments:', error);
      throw error;
    }
  }
  
  /**
   * Complete an application deployment
   * @param {string} deploymentId - Deployment ID
   * @returns {Promise<void>}
   */
  async completeDeployment(deploymentId) {
    try {
      const deploymentRef = doc(db, DEPLOYMENTS_COLLECTION, deploymentId);
      const deploymentDoc = await getDoc(deploymentRef);
      
      if (!deploymentDoc.exists()) {
        throw new Error(`Deployment with ID ${deploymentId} not found`);
      }
      
      const deploymentData = deploymentDoc.data();
      const now = new Date().toISOString();
      
      await updateDoc(deploymentRef, {
        status: 'completed',
        completedAt: now,
        updatedAt: now
      });
      
      // עדכון ה-userApps באופן ישיר
      if (deploymentData.fileId) {
        try {
          // מצא את מזהה המסמך של userApp לפי fileId
          const q = query(
            collection(db, 'userApps'),
            where('fileId', '==', deploymentData.fileId)
          );
          
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userAppDoc = querySnapshot.docs[0];
            // סטטוס קבוע ל-APP_LIVE בסיום התהליך
            await updateAppStatus(userAppDoc.id, AppStatus.APP_LIVE, `Deployment completed successfully`);
          }
        } catch (error) {
          console.error('Error updating userApp status:', error);
        }
      }
    } catch (error) {
      console.error('Error completing deployment:', error);
      throw error;
    }
  }
  
  /**
   * Cancel an application deployment
   * @param {string} deploymentId - Deployment ID
   * @param {string} reason - Cancellation reason
   * @returns {Promise<void>}
   */
  async cancelDeployment(deploymentId, reason) {
    try {
      const deploymentRef = doc(db, DEPLOYMENTS_COLLECTION, deploymentId);
      const deploymentDoc = await getDoc(deploymentRef);
      
      if (!deploymentDoc.exists()) {
        throw new Error(`Deployment with ID ${deploymentId} not found`);
      }
      
      const deploymentData = deploymentDoc.data();
      const now = new Date().toISOString();
      
      await updateDoc(deploymentRef, {
        status: 'cancelled',
        cancelledAt: now,
        cancellationReason: reason,
        updatedAt: now
      });
      
      // עדכון ה-userApps באופן ישיר
      if (deploymentData.fileId) {
        try {
          // מצא את מזהה המסמך של userApp לפי fileId
          const q = query(
            collection(db, 'userApps'),
            where('fileId', '==', deploymentData.fileId)
          );
          
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userAppDoc = querySnapshot.docs[0];
            // חזרה לסטטוס AWAITING_UPLOAD בביטול דיפלוימנט
            await updateAppStatus(userAppDoc.id, AppStatus.AWAITING_UPLOAD, `Deployment cancelled: ${reason}`);
          }
        } catch (error) {
          console.error('Error updating userApp status:', error);
        }
      }
    } catch (error) {
      console.error('Error cancelling deployment:', error);
      throw error;
    }
  }

  /**
   * Retrieve application data by file ID
   * @param {string} fileId - The file identifier
   * @returns {Promise<Object|null>} - Application data or null if not found
   */
  async getAppDataByFileId(fileId) {
    try {
      if (!fileId) {
        console.error('getAppDataByFileId: No fileId provided');
        return null;
      }
      
      // Query userApps collection to find app with matching fileId
      const q = query(
        collection(db, 'userApps'),
        where('fileId', '==', fileId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log(`No app found for fileId: ${fileId}`);
        return null;
      }
      
      // Get app data
      const appDoc = querySnapshot.docs[0];
      const appData = appDoc.data();
      
      // If we have a userId in the app data, try to get user info
      if (appData.userId) {
        try {
          const userDocRef = doc(db, 'users', appData.userId);
          const userDocSnapshot = await getDoc(userDocRef);
          
          if (userDocSnapshot.exists()) {
            const userData = userDocSnapshot.data();
            // Add user information to the app data
            return {
              ...appData,
              id: appDoc.id,
              userName: userData.displayName || appData.userName || null,
              userEmail: userData.email || appData.userEmail || null,
              userPhotoURL: userData.photoURL || null
            };
          }
        } catch (userErr) {
          console.error('Error getting user data:', userErr);
          // Continue with just the app data
        }
      }
      
      // Return app data without user information if we couldn't get it
      return {
        ...appData,
        id: appDoc.id
      };
    } catch (error) {
      console.error('Error getting app data by fileId:', error);
      throw error;
    }
  }
}

export default AppDeploymentsService; 